import React from 'react';
import { Card, Space, Table, Typography, Tag } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import { useGetIntegrityDetectorsQuery } from '../../../services/nestjs-base-api';
import {
    detectorName,
    profileDescription,
    profileName,
} from '../../components/response-integrity/integrityI18n';

const { Title, Text } = Typography;

interface ProfileRow {
    id: string;
    displayName: string;
    description: string | null;
    activeDetectors: string[];
}

export const IntegrityProfilesPage: React.FC = () => {
    const intl = useIntl();
    const { user } = useAuth();
    const { data, isLoading } = useGetIntegrityDetectorsQuery({
        jwtToken: user?.jwtToken || '',
    });

    const rows: ProfileRow[] = (data?.profiles ?? []).map((p: any) => {
        const activeDetectors = (data?.detectors ?? [])
            .filter((d: any) =>
                d.perProfile.some(
                    (pp: any) => pp.profileId === p.id && pp.isActive,
                ),
            )
            .map((d: any) => detectorName(intl, d.detectorId, d.displayName));
        return {
            id: p.id,
            displayName: profileName(intl, p.id, p.displayName),
            description: profileDescription(intl, p.id, p.description),
            activeDetectors,
        };
    });

    return (
        <div style={{ padding: 24 }}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <div>
                    <Title level={3} style={{ margin: 0 }}>
                        <SafetyCertificateOutlined style={{ marginRight: 8 }} />
                        <FormattedMessage id="responseIntegrity.profiles.title" />
                    </Title>
                    <Text type="secondary">
                        <FormattedMessage id="responseIntegrity.profiles.subtitle" />
                    </Text>
                </div>

                <Card styles={{ body: { padding: 0 } }}>
                    <Table<ProfileRow>
                        rowKey="id"
                        loading={isLoading}
                        dataSource={rows}
                        pagination={false}
                        size="small"
                        columns={[
                            {
                                title: <FormattedMessage id="responseIntegrity.profiles.col.id" />,
                                dataIndex: 'id',
                                width: 160,
                                render: (id: string) => <Text code>{id}</Text>,
                            },
                            {
                                title: <FormattedMessage id="responseIntegrity.profiles.col.name" />,
                                dataIndex: 'displayName',
                                width: 180,
                            },
                            {
                                title: <FormattedMessage id="responseIntegrity.profiles.col.description" />,
                                dataIndex: 'description',
                                render: (v: string | null) => v ?? '—',
                            },
                            {
                                title: <FormattedMessage id="responseIntegrity.profiles.col.activeDetectors" />,
                                dataIndex: 'activeDetectors',
                                render: (detectors: string[]) => (
                                    <Space size={[4, 4]} wrap>
                                        {detectors.map((d) => (
                                            <Tag key={d} style={{ margin: 0 }}>
                                                {d}
                                            </Tag>
                                        ))}
                                    </Space>
                                ),
                            },
                        ]}
                    />
                </Card>
            </Space>
        </div>
    );
};
